<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { PROMOTIONS } from 'src/constants';
	import { g1DHasValidExpiry } from 'src/helpers/home_helpers';
	import { save_profile } from 'src/helpers/profile_helpers';
	import { getPromoExpiryText } from 'src/helpers/promo_helpers';
	import { canManageHomeEntities } from 'src/helpers/houses/homes_helpers';
	import { profile_data, user_data } from 'src/stores/account/user';
	import { homes } from 'src/stores/account/home';
	import { format, locale } from 'svelte-i18n';
	import { modalStore } from 'src/stores/ui/modal_states';
	import { EnergyPriceSetupModalConfig } from 'src/configs/modals/EnergyPriceSetupModalConfig';
	import EnergyPriceAlert from 'src/components/Consumption/VerivoxEnergyPriceIntegration/components/alert/EnergyPriceAlert.svelte';
	import PromoDropdownContentWrapper from '../../PromoDropdownContentWrapper.svelte';
	import type { DiscountDetailsType } from 'src/types/DiscountType';

	// Props
	export let g1dItems: (DiscountDetailsType & { key: string })[] = [];
	export let g1dExpiryText: string = '';
	export let thirtyMillionExpiryText: string = '';
	export let xlX2FlashDealExpiryText: string = '';
	export let wallDisplayFlashSale2ExpiryText: string = '';
	export let springDeals2026ExpiryText: string = '';
	export let smartHomeChallenge2026ExpiryText: string = '';
	export let shellyBirthday2026ExpiryText: string = '';
	export let shouldShowEnergyPriceAlert: boolean = false;

	$: isMainUnitAccount = canManageHomeEntities($homes.list);
	$: isUsMarket = $user_data?.timezone?.startsWith('America/');
	const supportedImageLocales = ['de', 'fr', 'it'];
	const getBirthdayImageLocale = (isUsMarket: boolean, selectedLocale: string) => {
		if (isUsMarket) return 'default';
		return supportedImageLocales.includes(selectedLocale) ? selectedLocale : 'default';
	};
	$: springDeals2026MaxDiscount = isUsMarket ? 50 : 40;
	$: springDeals2026Thumbnail = isUsMarket
		? './images/shelly_spring_deals_2026_App_thumbnail_us.png'
		: './images/shelly_spring_deals_2026_App_thumbnail.png';
	$: smartHomeChallenge2026Thumbnail = isUsMarket
		? './images/ioT-Challenge-thumbnail-us.png'
		: `./images/ioT-Challenge-thumbnail-${['de', 'fr', 'it'].includes($locale) ? $locale : 'default'}.png`;
	$: smartHomeChallenge2026PrizeLabel = isUsMarket ? '$500' : '500€';
	$: shellyBirthday2026Thumbnail = `./images/Shelly_BD_8y_KV_Adaptations_for_App_badge_${getBirthdayImageLocale(
		!!isUsMarket,
		$locale,
	)}.png`;

	// Events
	const dispatch = createEventDispatcher<{
		showGen1Replacement: void;
		showThirtyMillionDevices: void;
		showXlX2FlashDeal: void;
		showWallDisplayFlashSale2: void;
		showSpringDeals2026: void;
		showSmartHomeChallenge: void;
		showSmartHomeChallenge2026: void;
		showShellyBirthday2026: void;
		showBirthdayTeaser: void;
		showBirthdayCampaign: void;
		showBlackFridayPhase1: void;
		showBlackFridayPhase2: void;
		showAnnualSurvey2026: void;
		closeMenu: void;
	}>();

	const handlePromoClick = (eventName: string) => {
		dispatch('closeMenu');
		dispatch(eventName as any);

		if (eventName === 'showThirtyMillionDevices') {
			const thm = { ...$profile_data?.aep?.thm };
			thm.seen = new Date().getTime();
			thm.clickOnDropdownMenuItem = (thm?.clickOnDropdownMenuItem || 0) + 1;
			save_profile('aep', { ...$profile_data.aep, thm });
		}
		if (eventName === 'showXlX2FlashDeal') {
			const xlx2 = { ...$profile_data?.aep?.xlx2 };
			xlx2.seen = new Date().getTime();
			xlx2.clickOnDropdownMenuItem = (xlx2?.clickOnDropdownMenuItem || 0) + 1;
			save_profile('aep', { ...$profile_data.aep, xlx2 });
		}
		if (eventName === 'showWallDisplayFlashSale2') {
			const wdfs2 = { ...$profile_data?.aep?.wdfs2 };
			wdfs2.seen = new Date().getTime();
			wdfs2.clickOnDropdownMenuItem = (wdfs2?.clickOnDropdownMenuItem || 0) + 1;
			save_profile('aep', { ...$profile_data.aep, wdfs2 });
		}
		if (eventName === 'showSpringDeals2026') {
			const sd = { ...$profile_data?.aep?.sd };
			sd.seen = new Date().getTime();
			sd.clickOnDropdownMenuItem = (sd?.clickOnDropdownMenuItem || 0) + 1;
			save_profile('aep', { ...$profile_data.aep, sd });
		}
		if (eventName === 'showSmartHomeChallenge2026') {
			const shc26 = { ...$profile_data?.aep?.shc26 };
			shc26.seen = new Date().getTime();
			shc26.clickOnDropdownMenuItem = (shc26?.clickOnDropdownMenuItem || 0) + 1;
			save_profile('aep', { ...$profile_data.aep, shc26 });
		}
		if (eventName === 'showShellyBirthday2026') {
			const bd26 = { ...$profile_data?.aep?.bd26 };
			bd26.seen = new Date().getTime();
			bd26.clickOnDropdownMenuItem = (bd26.clickOnDropdownMenuItem || 0) + 1;
			save_profile('aep', { ...$profile_data.aep, bd26 });
		}
	};
</script>

<div class="d-flex flex-direction-column gap-m">
	{#if g1dItems.length > 0 && g1DHasValidExpiry(g1dItems) && new Date().getTime() >= PROMOTIONS.GEN_1_REPLACEMENT.START_DATE && new Date().getTime() <= PROMOTIONS.GEN_1_REPLACEMENT.END_DATE}
		<PromoDropdownContentWrapper
			icon="fas fa-percent"
			title={$format('new_title_gen1_replacement', {
				default: 'Gen1 Upgrade Programme',
			})}
			text={$format('new_gen_1_replacement_message', {
				default: 'You have been chosen for discount to upgrade your Gen1 devices',
			})}
			expiryText={g1dExpiryText}
			hasNotification={g1dItems.filter((item) => !item.seen).length > 0}
			on:click={() => handlePromoClick('showGen1Replacement')}
		/>
	{/if}
	{#if new Date().getTime() >= PROMOTIONS.THIRTY_MILLION_DEVICES.START_DATE && new Date().getTime() < PROMOTIONS.THIRTY_MILLION_DEVICES.END_DATE}
		<PromoDropdownContentWrapper
			image="./images/30m_thumbnail.png"
			title={$format('heading_30_million_devices', {
				default: '30 Million Smart Decisions',
			})}
			text={$format('text_30_million_devices_message', {
				default: 'Up to 30% Off. Buy more. Save more!',
			})}
			expiryText={thirtyMillionExpiryText}
			hasNotification={!$profile_data?.promotions?.thirtyMillionDevices}
			on:click={() => handlePromoClick('showThirtyMillionDevices')}
		/>
	{/if}
	{#if (!!$profile_data?.aep?.xlx2?.ts || import.meta.env.DEV) && new Date().getTime() >= PROMOTIONS.XL_X2_FLASH_DEAL.START_DATE && new Date().getTime() < PROMOTIONS.XL_X2_FLASH_DEAL.END_DATE}
		<PromoDropdownContentWrapper
			image="./images/xl_x2_flash_deal_thumbnail.png"
			title={$format('title_wall_display_flash_sale', {
				default: 'WallDisplay Flash Sale',
			})}
			text={$format('text_wall_display_flash_sale_message', {
				default: 'Your smart home deserves a control center.',
			})}
			expiryText={xlX2FlashDealExpiryText}
			hasNotification={!$profile_data?.promotions?.xlX2FlashDeal}
			on:click={() => handlePromoClick('showXlX2FlashDeal')}
		/>
	{/if}
	{#if (!!$profile_data?.aep?.wdfs2?.code || import.meta.env.DEV) && isMainUnitAccount && new Date().getTime() >= PROMOTIONS.WALL_DISPLAY_FLASH_SALE_2.START_DATE && new Date().getTime() < PROMOTIONS.WALL_DISPLAY_FLASH_SALE_2.END_DATE}
		<PromoDropdownContentWrapper
			image="./images/wall_display_flash__deal_thumbnail.png"
			title={$format('title_wall_display_flash_sale_2', {
				default: 'Wall Display Flash Sale',
			})}
			text={$format('text_wall_display_flash_sale_2_message', {
				default: 'An Exclusive Offer for Our Most Loyal Users',
			})}
			expiryText={wallDisplayFlashSale2ExpiryText}
			hasNotification={!$profile_data?.promotions?.wallDisplayFlashSale2}
			on:click={() => handlePromoClick('showWallDisplayFlashSale2')}
		/>
	{/if}
	{#if new Date().getTime() >= PROMOTIONS.SPRING_DEALS_2026.START_DATE && new Date().getTime() < PROMOTIONS.SPRING_DEALS_2026.END_DATE}
		<PromoDropdownContentWrapper
			image={springDeals2026Thumbnail}
			title={$format('title_spring_deals', {
				default: 'Spring Deals',
			})}
			text={$format('text_spring_deals_message', {
				default: 'Fresh season. Best prices. Up to {percent}% off.',
				values: { percent: springDeals2026MaxDiscount },
			})}
			expiryText={springDeals2026ExpiryText}
			hasNotification={!$profile_data?.promotions?.springDeals2026}
			on:click={() => handlePromoClick('showSpringDeals2026')}
		/>
	{/if}
	{#if new Date().getTime() >= PROMOTIONS.SMART_HOME_CHALLENGE_2026.START_DATE && new Date().getTime() < PROMOTIONS.SMART_HOME_CHALLENGE_2026.END_DATE}
		<PromoDropdownContentWrapper
			image={smartHomeChallenge2026Thumbnail}
			title={$format('title_smart_home_challenge_2026', {
				default: 'Smart Home Challenge 2026',
			})}
			text={$format('text_smart_home_challenge_2026_message', {
				default: 'Submit Your Project And Win Attractive Prizes!',
				values: { prize: smartHomeChallenge2026PrizeLabel },
			})}
			expiryText={smartHomeChallenge2026ExpiryText}
			hasNotification={!$profile_data?.promotions?.smartHomeChallenge2026}
			on:click={() => handlePromoClick('showSmartHomeChallenge2026')}
		/>
	{/if}
	{#if new Date().getTime() >= PROMOTIONS.SHELLY_BIRTHDAY_2026.START_DATE && new Date().getTime() < PROMOTIONS.SHELLY_BIRTHDAY_2026.END_DATE}
		<PromoDropdownContentWrapper
			image={shellyBirthday2026Thumbnail}
			title={$format('title_shelly_birthday_2026', {
				default: '8 years deserve big deals',
			})}
			text={$format(
				isUsMarket ? 'text_shelly_birthday_2026_intro_us' : 'text_shelly_birthday_2026_intro',
				{
					default: isUsMarket
						? 'Celebrate with us and enjoy up to 50% off, free gifts, and more'
						: 'Celebrate with us and enjoy up to -50%, free gifts, and more',
				},
			)}
			expiryText={shellyBirthday2026ExpiryText}
			hasNotification={!$profile_data?.promotions?.shellyBirthday2026}
			on:click={() => handlePromoClick('showShellyBirthday2026')}
		/>
	{/if}
	{#if new Date().getTime() >= PROMOTIONS.SMART_HOME_CHALLENGE.START_DATE && new Date().getTime() < PROMOTIONS.SMART_HOME_CHALLENGE.END_DATE}
		<PromoDropdownContentWrapper
			icon="fas fa-trophy-star"
			title={$format('heading_smart_home_challenge', {
				default: 'Join the Shelly Smart Home Challenge',
			})}
			text={$format('message_smart_home_challenge', {
				default: 'Show off your ideas and win!',
			})}
			expiryText={getPromoExpiryText(PROMOTIONS.SMART_HOME_CHALLENGE.END_DATE)}
			on:click={() => handlePromoClick('showSmartHomeChallenge')}
		/>
	{:else if new Date().getTime() >= PROMOTIONS.SHELLY_BIRTHDAY_TEASER.START_DATE && new Date().getTime() < PROMOTIONS.SHELLY_BIRTHDAY_TEASER.END_DATE}
		<PromoDropdownContentWrapper
			icon="fas fa-gift"
			title={$format('alert_heading_birthday_invitation', {
				default: "It's our birthday soon-and you're invited!",
			})}
			text={$format('alert_message_join_for_present', {
				default: 'Join the VIP guest list and get your special present!',
			})}
			expiryText={getPromoExpiryText(PROMOTIONS.SHELLY_BIRTHDAY_TEASER.END_DATE)}
			on:click={() => handlePromoClick('showBirthdayTeaser')}
		/>
	{:else if new Date().getTime() >= PROMOTIONS.SHELLY_BIRTHDAY_CAMPAIGN.START_DATE && new Date().getTime() < PROMOTIONS.SHELLY_BIRTHDAY_CAMPAIGN.END_DATE}
		<PromoDropdownContentWrapper
			icon="fas fa-cake-candles"
			title={$format('alert_heading_shelly_birthday', {
				default: 'Happy Shelly Birthday!',
			})}
			text={$format('alert_message_seven_years', {
				default: '7 Years of Innovation, 7 Days of Celebration',
			})}
			expiryText={getPromoExpiryText(PROMOTIONS.SHELLY_BIRTHDAY_CAMPAIGN.END_DATE)}
			on:click={() => handlePromoClick('showBirthdayCampaign')}
		/>
	{:else if new Date().getTime() >= PROMOTIONS.BLACK_FRIDAY_PHASE_1.START_DATE && new Date().getTime() < PROMOTIONS.BLACK_FRIDAY_PHASE_1.END_DATE}
		<PromoDropdownContentWrapper
			icon="fas fa-star"
			title={$format('alert_heading_black_friday_phase_1', {
				default: 'Your VIP Black Friday access awaits',
			})}
			text={$format('alert_message_black_friday_phase_1', {
				default: 'Join the exclusive guest list and unlock secret perks!',
			})}
			expiryText={getPromoExpiryText(PROMOTIONS.BLACK_FRIDAY_PHASE_1.END_DATE)}
			on:click={() => handlePromoClick('showBlackFridayPhase1')}
		/>
	{:else if new Date().getTime() >= PROMOTIONS.ANNUAL_SURVEY_2026.START_DATE && new Date().getTime() < PROMOTIONS.ANNUAL_SURVEY_2026.END_DATE}
		<PromoDropdownContentWrapper
			icon="fas fa-user-microphone"
			title={$format('subtitle_one_annual_survey', {
				default: "It's feedback time!",
			})}
			text={$format('alert_message_annual_survey_2026', {
				default: 'Join our quick survey and enjoy 15% off your next Shelly order',
			})}
			expiryText={getPromoExpiryText(PROMOTIONS.ANNUAL_SURVEY_2026.END_DATE)}
			on:click={() => handlePromoClick('showAnnualSurvey2026')}
		/>
	{:else if new Date().getTime() >= PROMOTIONS.BLACK_FRIDAY_PHASE_2.START_DATE && new Date().getTime() < PROMOTIONS.BLACK_FRIDAY_PHASE_2.END_DATE}
		<PromoDropdownContentWrapper
			icon="fas fa-badge-percent"
			title={$format('alert_heading_black_friday_phase_2', {
				default: 'Black Friday starts now',
			})}
			text={$format('alert_message_black_friday_phase_2', {
				default: 'Smart tech. Smarter savings.',
			})}
			expiryText={getPromoExpiryText(PROMOTIONS.BLACK_FRIDAY_PHASE_2.END_DATE)}
			on:click={() => handlePromoClick('showBlackFridayPhase2')}
		/>
	{/if}
	{#if shouldShowEnergyPriceAlert}
		<EnergyPriceAlert
			onCtaClick={() => {
				dispatch('closeMenu');
				modalStore.showModal(EnergyPriceSetupModalConfig());
			}}
		/>
	{/if}
</div>
